import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Activity, Phone, ShieldAlert, Target, Zap } from "lucide-react";
import { useFetch } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { CountUp, Skeleton, StatusBadge, VerdictBadge } from "@/components/shared";
import { useDemoMode } from "@/lib/demo";

const Stat = ({ icon: Icon, label, value, suffix = "", decimals = 0, accent, i }) => (
  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }} className="panel p-5 min-h-[112px]" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-center justify-between">
      <p className="eyebrow">{label}</p>
      <Icon size={15} style={{ color: accent }} />
    </div>
    <p className="mt-3 font-heading text-3xl font-bold tabular-nums">
      {value === null || value === undefined ? <span className="text-white/30">—</span> : <CountUp value={value} decimals={decimals} suffix={suffix} />}
    </p>
  </motion.div>
);

export default function CommandCenter() {
  const { data, loading } = useFetch("/stats", { poll: 4000 });
  const [demo] = useDemoMode();
  return (
    <div className="space-y-10" data-testid="command-center">
      <section className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <div>
          <p className="eyebrow mb-3">AI Voice Agent Stress-Tester</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02]">
            Break your agent<br /><span className="grad-text">before your customers do.</span>
          </h1>
          <p className="mt-4 max-w-xl text-sm md:text-base text-white/60">Eight adversarial AI customers call your script in parallel. A third model judges every turn. You get a Pilot Readiness verdict in minutes.</p>
        </div>
        <Link to="/new" className="btn-primary self-start lg:self-auto" data-testid="launch-gauntlet-cta"><Zap size={16} /> Launch a Gauntlet <ArrowRight size={16} /></Link>
      </section>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-tour="stats">
        <Stat i={0} icon={Target} label="Avg readiness" value={data?.avg_readiness} decimals={1} accent="#22D3EE" />
        <Stat i={1} icon={Activity} label="Pass rate" value={data?.pass_rate} suffix="%" accent="#34D399" />
        <Stat i={2} icon={Phone} label="Calls simulated" value={data?.calls} accent="#8B5CF6" />
        <Stat i={3} icon={ShieldAlert} label="Compliance breaches" value={data?.compliance_breaches} accent="#F87171" />
      </section>

      <section className="panel p-6" data-tour="recent" data-testid="recent-tests">
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="eyebrow">Recent tests</p>
            <h2 className="text-base md:text-lg font-semibold mt-1">{data?.running ? `${data.running} running now` : "Latest runs"}</h2>
          </div>
          <Link to="/history" className="text-xs text-white/50 hover:text-white inline-flex items-center gap-1" data-testid="view-all-history">View all <ArrowRight size={12} /></Link>
        </div>
        {loading && !data ? (
          <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-14" />)}</div>
        ) : data?.recent?.length ? (
          <ul className="divide-y divide-white/[0.06]">
            {data.recent.map((t, i) => (
              <motion.li key={t.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}>
                <Link to={t.status === "completed" ? `/tests/${t.id}/report` : `/arena/${t.id}`} className="flex items-center gap-4 py-3.5 hover:bg-white/[0.02] -mx-3 px-3 rounded-xl transition-colors" data-testid={`recent-test-${t.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{t.name}</p>
                    <p className="text-xs text-white/45 mt-0.5">{fmtDate(t.created_at)} · {t.personas} personas · v{t.versions}{t.demo ? " · demo" : ""}</p>
                  </div>
                  <span className="hidden sm:inline font-mono text-sm tabular-nums text-white/80">{t.readiness ?? "—"}</span>
                  <VerdictBadge verdict={t.verdict} />
                  <StatusBadge status={t.status} />
                </Link>
              </motion.li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-white/45 py-6">No tests yet. {demo ? "Demo mode is on: launch a test to see an instant replay." : "Launch your first Gauntlet."}</p>
        )}
      </section>
    </div>
  );
}
